import { getRenderingEngine, utilities as csUtils } from "@cornerstonejs/core";
import {
  CrosshairsTool,
  ToolGroupManager,
  Enums as csToolsEnums,
} from "@cornerstonejs/tools";
import getIds from "lib/cornerstone/helpers/getIds";
import { proxy } from "valtio";
import { setProgrammaticVOIChange } from "lib/cornerstone/helpers/voiState";

const { MouseBindings } = csToolsEnums;

interface WindowLevel {
  width: number;
  center: number;
}

interface SettingsState {
  windowLevel: WindowLevel;
  defaultWindowLevel: WindowLevel;
  roiOpacity: number;
  defaultRoiOpacity: number;
  showOrientationLabel: boolean;
  showCrossHair: boolean;
}

const DEFAULT_ORIENTATION_LABEL = true;

const settingsState = proxy<SettingsState>({
  windowLevel: { width: 500, center: 500 },
  defaultWindowLevel: { width: 500, center: 500 },
  roiOpacity: 100,
  defaultRoiOpacity: 100,
  showOrientationLabel: DEFAULT_ORIENTATION_LABEL,
  showCrossHair: true,
});

let onRoiOpacityChange: ((value: number) => void) | null = null;

export function registerRoiOpacityCallback(callback: (value: number) => void) {
  onRoiOpacityChange = callback;
}

export function updateRoiOpacity(value: number, setDefault: boolean = false) {
  if (settingsState.roiOpacity === value && !setDefault) return;
  settingsState.roiOpacity = value;

  if (setDefault) {
    settingsState.defaultRoiOpacity = value;
  }

  onRoiOpacityChange?.(value);
}

export function updateWindowLevel(
  newWindowLevel: Partial<WindowLevel>,
  setDefault: boolean = false,
) {
  const newWidth = newWindowLevel.width ?? settingsState.windowLevel.width;
  const newCenter = newWindowLevel.center ?? settingsState.windowLevel.center;

  if (
    newWidth === settingsState.windowLevel.width &&
    newCenter === settingsState.windowLevel.center &&
    !setDefault
  ) {
    return;
  }

  settingsState.windowLevel = { width: newWidth, center: newCenter };

  if (setDefault) {
    settingsState.defaultWindowLevel = { width: newWidth, center: newCenter };
  }

  updateRendererWindowLevel(newWidth, newCenter);
}

export function toogleOrientationLabel() {
  settingsState.showOrientationLabel = !settingsState.showOrientationLabel;
}

export function toggleCrossHair() {
  const { toolGroupId } = getIds();
  const toolGroup = ToolGroupManager.getToolGroup(toolGroupId);

  if (!toolGroup) {
    console.warn("No Toolgroup");
    return;
  }

  settingsState.showCrossHair = !settingsState.showCrossHair;

  if (!settingsState.showCrossHair) {
    toolGroup.setToolDisabled(CrosshairsTool.toolName);
  } else {
    toolGroup.setToolActive(CrosshairsTool.toolName, {
      bindings: [{ mouseButton: MouseBindings.Primary }],
    });
  }
}

export const disableCrossHair = toggleCrossHair;

export function resetSettings() {
  const { renderingEngineId, viewportId1 } = getIds();
  const renderingEngine = getRenderingEngine(renderingEngineId);

  let defWidth = settingsState.defaultWindowLevel.width;
  let defCenter = settingsState.defaultWindowLevel.center;

  if (renderingEngine) {
    try {
      const vp = renderingEngine.getViewport(viewportId1) as any;
      const props = vp?.getProperties?.();
      if (props?.voiRange && defWidth === 500 && defCenter === 500) {
        const { lower, upper } = props.voiRange;
        const wl = csUtils.windowLevel.toWindowLevel(lower, upper);
        defWidth = Math.round(wl.windowWidth);
        defCenter = Math.round(wl.windowCenter);
        settingsState.defaultWindowLevel = {
          width: defWidth,
          center: defCenter,
        };
      }
    } catch (e) {}
  }

  settingsState.windowLevel = { width: defWidth, center: defCenter };
  updateRendererWindowLevel(defWidth, defCenter);

  updateRoiOpacity(settingsState.defaultRoiOpacity);

  settingsState.showOrientationLabel = DEFAULT_ORIENTATION_LABEL;

  if (!settingsState.showCrossHair) {
    const { toolGroupId } = getIds();
    const toolGroup = ToolGroupManager.getToolGroup(toolGroupId);
    if (toolGroup) {
      toolGroup.setToolActive(CrosshairsTool.toolName, {
        bindings: [{ mouseButton: MouseBindings.Primary }],
      });
    }
    settingsState.showCrossHair = true;
  }
}

function updateRendererWindowLevel(width: number, center: number) {
  const { renderingEngineId, volumeId } = getIds();
  const renderingEngine = getRenderingEngine(renderingEngineId);

  if (!renderingEngine) {
    return;
  }

  const { lower, upper } = csUtils.windowLevel.toLowHighRange(width, center);
  setProgrammaticVOIChange(true);

  const viewports = renderingEngine.getViewports();

  viewports.forEach((viewport: any) => {
    try {
      if (typeof viewport.setProperties === "function") {
        viewport.setProperties({ voiRange: { lower, upper } });
      }
    } catch (e) {
      try {
        const actor = viewport.getActor?.(volumeId)?.actor;
        if (actor?.getProperty) {
          const rgbTransferFunction = actor
            .getProperty()
            .getRGBTransferFunction?.(0);
          rgbTransferFunction?.setMappingRange?.(lower, upper);
        }
      } catch (e2) {}
    }
  });

  renderingEngine.render();

  setTimeout(() => {
    setProgrammaticVOIChange(false);
  }, 50);
}

export default settingsState;
